<template>
<v-card flat>
    <v-text-field
        v-model='searchString'
        :loading="searchRequest.loading"
        box clearable
        hint='Start typing a code or description'
        label="Search for a process code" >
    </v-text-field>
    <!-- <pre>{{suggestions}}</pre> -->
    <div class='mx-2' >
      <v-dialog v-model='resetSuggestionsDialog' width='300' >
        <v-card v-if="resetSuggestionsDialog" >

          <request-status
            :request="requests.setsuggestions"
            message200="Suggestions have been updated"
            @ok="resetSuggestionsDialog=false" />

          <v-card-title class='overline grey lighten-4 py-0' >
            Clear suggestions
            <v-spacer />
            <v-btn icon small @click="resetSuggestionsDialog=false" >
              <v-icon small >close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider/>
          <v-card-text class="pb-0 pt-1 caption" >
            Don't suggest codes/prices from appointments before this date.
            Use this if you have changed your pricing and no longer want suggestions with the old pricing
          </v-card-text>
          <v-date-picker v-model='suggestionDate' no-title flat full-width class='elevation-0' />
          <v-divider/>
          <v-card-actions >
            <v-btn flat >Cancel</v-btn>
            <v-spacer/>
            <v-btn color='primary'
              @click='setSuggestionDate'
              :loading="requests.setsuggestions.loading" >
              Update suggestions</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-subheader class='pl-2 overline'>
        Recently used <template v-if="afterDate" >since {{afterDate}}</template>
        <v-spacer/>
        <v-btn small label outline
          color='primary'
          class="py-0"
          @click='resetSuggestionsDialog=true' >Reset suggestions
        </v-btn>
      </v-subheader>
      <v-chip small label
        v-for='(code, index) in filteredSuggestions'
        @click='selectItem(code)'
        :key='index' ><strong>{{code.name}}</strong>&nbsp;
        {{formatCurrency('ZAR', (parseFloat(code.price))) }}
      </v-chip>
      <div v-if="filteredSuggestions && filteredSuggestions.length === 0" class="caption px-2" >
        No codes to suggest based on your appointments
        <template v-if="afterDate" >after {{afterDate}}</template>
      </div>
    </div>

    <v-list v-if='searchString && searchString.length > 0' dense two-line >
      <v-subheader class="overline" >Search results</v-subheader>
      <template v-for='result in searchResults' >
        <v-list-tile @click='selectItem(result)' :key='`li-${result.id}`'>
          <v-list-tile-content>
            <v-list-tile-title><strong>{{result.name}}</strong></v-list-tile-title>
            <v-list-tile-sub-title>{{result.description}}</v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            {{formatCurrency('ZAR', (parseFloat(result.discovery_rate))) }}
          </v-list-tile-action>
        </v-list-tile>
        <v-divider :key='`div-${result.id}`' ></v-divider>
      </template>
      <v-list-tile v-if='searchResults && searchResults.length === 0'>
        <v-list-tile-content>No codes matched your query</v-list-tile-content>
      </v-list-tile>
    </v-list>
  </v-card>
</template>

<script>
import {
  CODE_SUGGESTIONS
} from '@/store/constants'

import debounce from '@/mixins/debounce'
import RequestStatus from '../ui/RequestStatus.vue'
export default {
  components: { RequestStatus },
  name: 'ProcessCodePicker',
  props: {
    value: { type: Object, default: () => { return null } },
    initialSearchString: { type: String, default: null },
    maxSuggestions: { type: Number, default: 15 },
    withSuggestions: { type: Boolean, default: true }
  },
  data () {
    return {
      selected: null,
      searchString: '',
      searchReqId: 'processcode-search-request',
      suggestionsReqId: CODE_SUGGESTIONS,
      resetSuggestionsDialog: false,
      suggestionDate: null,
      requests: {
        setsuggestions: {
          resource: 'ag.practitioner',
          extra: 'actions/clear_code_suggestions',
          method: 'post'
        }
      }
    }
  },
  mounted () {
    this.search()
    if (this.suggestions && this.suggestions.length === 0) {
      this.getSuggestions()
    }
    this.suggestionDate = new Date().toISOString().split('T')[0]
  },
  computed: {
    afterDate () {
      return this.getObjectPath(this.suggestionsRequest, 'result.data.codes.meta.since', false)
    },
    suggestions () {
      // return this.getObjectPath(this.suggestionsRequest, 'result.data.codes', [])
      if (this.suggestionsRequest &&
          this.suggestionsRequest.result &&
          this.suggestionsRequest.result.data &&
          this.suggestionsRequest.result.data.codes) {
        return this.suggestionsRequest.result.data.codes
      }
      return []
    },
    filteredSuggestions () {
      let hasSearchCriteria = (this.searchString && this.searchString.length > 0)
      let results = this.suggestions.process
      if (!results) { return [] }
      if (hasSearchCriteria) {
        results = this.suggestions.process.filter((item) => {
          if (item.name.startsWith(this.searchString)) {
            return item
          }
        })
      }
      if (this.maxSuggestions !== 0) {
        return results.slice(0, this.maxSuggestions)
      }
      return results
    },
    setsuggestionsRequestStatus () {
      return this.requests.setsuggestions.status
    },
    suggestionsRequest () {
      return this.$store.getters.getRequestById(this.suggestionsReqId, true)
    },
    searchRequest () {
      return this.$store.getters.getRequestById(this.searchReqId, false)
    },
    searchResults () {
      if (this.searchRequest &&
          this.searchRequest.result &&
          this.searchRequest.result.data &&
          this.searchRequest.result.data.results) {
        return this.searchRequest.result.data.results
      }
      return []
    }
  },
  watch: {
    searchString () {
      this.search()
    },
    initialSearchString () {
      this.searchString = this.initialSearchString
    },
    setsuggestionsRequestStatus () {
      if (this.setsuggestionsRequestStatus === 200) {
        this.getSuggestions()
      }
    }
  },
  methods: {
    selectItem (item) {
      this.selected = item
      this.$emit('input', item)
    },
    setSuggestionDate () {
      this.requests.setsuggestions.data = {date: this.suggestionDate}
      this.requests.setsuggestions.resourceId = this.meId
      this.$request('save', this.requests.setsuggestions)
    },
    getSuggestions: debounce(function () {
      this.$appointmentguru.resource('appointment.codes.suggestions').id(this.suggestionsReqId).list()
    }, 500),
    search: debounce(function () {
      if (this.searchString && this.searchString.length > 0) {
        let params = {
          search: this.searchString
        }
        this.$codeguru
          .resource('processcode')
          .id(this.searchReqId)
          .list(params)
      }
    }, 500)
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// ProcessCodePicker.vue?9893f392